<template>
  <v-container fluid>
    <v-data-iterator
      :items="items"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      :search="search"
      :sort-by="sortBy.toLowerCase()"
      :sort-desc="sortDesc"
      hide-default-footer
      :loading="loadingProjects"
    >
      <template v-slot:header>
        <v-toolbar dark color="blue darken-3" class="mb-1">
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <v-btn-toggle v-model="sortDesc" mandatory>
              <v-btn large depressed color="blue" :value="false">
                <v-icon>mdi-arrow-up</v-icon>
              </v-btn>
              <v-btn
                large
                depressed
                color="blue"
                :value="true"
              >
                <v-icon>mdi-arrow-down</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-select
              v-model="sortBy"
              flat
              solo-inverted
              hide-details
              :items="keys"
              prepend-inner-icon="mdi-magnify"
              label="Sort by"
              clearable
            ></v-select>
            <v-spacer></v-spacer>
            <v-dialog v-model="addProject" max-width="700px">
              <template v-slot:activator="{ on: dialog, attrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      id="add-project-btn"
                      large
                      depressed
                      color="blue"
                      v-on="{ ...dialog, ...tooltip}"
                      v-bind="attrs"
                    ><v-icon>add</v-icon></v-btn>
                  </template>
                  Add a Project
                </v-tooltip>
              </template>
              <CreateProjectForm
                :cid="currentCompany.cid"
                :teams="allTeams"
                v-on:closeDialog="close"
              />
            </v-dialog>
          </template>
        </v-toolbar>
      </template>

      <template v-slot:default="props">
        <v-row>
          <v-col v-for="item in props.items" :key="item.pid" cols="12" sm="6" md="4" lg="4">
            <v-card>
              <v-card-title class="subheading font-weight-bold">
                {{ item.name }}
              </v-card-title>
              <v-card-subtitle>
                Reviewer:
                {{
                  (hasReviewer(item.first_reviewer))
                  ? item.first_reviewer.name
                  : 'No reviewer assigned for this project'
                }}
              </v-card-subtitle>
              <v-divider></v-divider>
              <v-list dense style="overflow-y: auto; height: 225px">
                <v-list-item
                  v-for="(key, index) in filteredKeys"
                  :key="index"
                  class="body-1"
                >
                  <v-list-item-content :class="{ 'blue--text' : sortBy === key }">
                    <v-icon>{{ }}</v-icon>{{ key }}:
                  </v-list-item-content>
                  <v-list-item-content
                    v-if="key !== 'Teams'"
                    class="align-end"
                    :class="{ 'blue--text' : sortBy === key }"
                  >
                    {{ (item[key.toLowerCase()].length > 0)
                      ? item[key.toLowerCase()] : `No ${key.toLowerCase()} was entered in` }}
                  </v-list-item-content>
                  <v-list-item-content
                    v-if="key === 'Teams'"
                    class="align-end"
                    :class="{ 'blue--text' : sortBy === key }"
                  >
                    {{ getTeams(item) }}
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-divider light style="margin-top: 15px"></v-divider>
              <v-divider light></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn x-small color="primary" :to="`/project/${item.pid}`">
                <span>View Project</span>
                  <v-icon x-small>visibility</v-icon>
                </v-btn>
                <ActionConfirmation
                  color="error"
                  :title="`Deleting ${item.name}`"
                  :content="`Are you sure you want to delete the project: ${item.name}?`"
                  buttonText="Delete"
                  :id="item.pid"
                  icon="delete"
                  buttonType="combination"
                  v-on:confirm="confirmDeleteProject"
                  :isConfirming="isDeleting"
                  ref="confirmation"
                />
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <template v-slot:footer>
        <v-row class="mt-2" align="center" justify="center">
          <span class="grey--text">Items per page</span>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn dark text color="primary" class="ml-2" v-bind="attrs" v-on="on">
                {{ itemsPerPage }}
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(number, index) in itemsPerPageArray"
                :key="index"
                @click="updateItemsPerPage(number)"
              >
                <v-list-item-title>{{ number }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-spacer></v-spacer>
          <span class="mr-4 grey--text">Page {{ page }} of {{ numberOfPages }}</span>
          <v-btn fab dark color="blue darken-3" class="mr-1" @click="formerPage">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn fab dark color="blue darken-3" class="ml-1" @click="nextPage">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-row>
      </template>
    </v-data-iterator>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import CreateProjectForm from '@components/projects/CreateProjectForm.vue';
import ActionConfirmation from '@components/common/ActionConfirmation.vue';

export default {
  name: 'ProjectsDataIterators',
  components: { ActionConfirmation, CreateProjectForm },
  data: () => ({
    itemsPerPageArray: [6, 12, 18],
    addProject: false,
    search: '',
    filter: {},
    sortDesc: false,
    isDeleting: false,
    page: 1,
    itemsPerPage: 6,
    sortBy: 'name',
    keys: ['Name', 'Location', 'Description', 'Date', 'Teams'],
    items: [],
  }),
  computed: {
    ...mapGetters(['allProjects', 'allTeams', 'loadingProjects', 'currentProjectSearch', 'currentCompany', 'companyHas']),
    numberOfPages() { return Math.ceil(this.items.length / this.itemsPerPage); },
    filteredKeys() { return this.keys.filter((key) => key !== 'Name'); },
  },
  watch: {
    currentProjectSearch(newSearch) { this.search = newSearch; },
    allProjects(newProjectList) {
      this.items = newProjectList;
    },
  },
  methods: {
    ...mapActions(['deleteProject', 'updateTeam']),
    nextPage() { if (this.page + 1 <= this.numberOfPages) this.page += 1; },
    formerPage() { if (this.page - 1 >= 1) this.page -= 1; },
    close() { this.addProject = false; },
    hasReviewer: (reviewer) => {
      if (!reviewer) return false;
      return Object.keys(reviewer).length > 0;
    },
    updateItemsPerPage(number) { this.itemsPerPage = number; },
    getTeams(item) {
      if (item.teams.length === 0) return 'This project has not been assigned a team';
      return item.teams.map((team) => team.name).join(', ');
    },
    confirmDeleteProject(pid) {
      this.isDeleting = true;
      const [deletingProject] = this.allProjects.filter((project) => project.pid === pid);
      this.deleteProject(deletingProject)
        .then((res) => {
          this.isDeleting = false;
          // this.$refs.confirmation.close();
          const deletedProject = res.data;
          deletedProject.teams.forEach(async (team) => {
            const currentTeam = team;
            currentTeam.projects = currentTeam.projects.filter((p) => p.pid !== deletedProject.pid);
            await this.updateTeam(currentTeam);
          });
        });
    },
  },
  mounted() {
    this.items = cloneDeep(this.allProjects);
  },
};
</script>
